import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ScoreShield from '../components/trip/ScoreShield';

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function loadReport() {
      try {
        const res = await api.get(`/classify/search/${id}`);
        setReport(res.data);
      } catch (error) {
        toast.error('Failed to load report');
        navigate('/search');
      } finally {
        setLoading(false);
      }
    }
      loadReport();
  }, [id, navigate]);

    // Poll for TRIP completion when a background TRIP run is pending.
    // Binary check: re-fetch the search every 5s until trip_status resolves.
    const tripPollRef = useRef(null);
    useEffect(() => {
        if (!report) return;
        // Only poll while pending
        if (report.trip_status !== 'pending') return;

        tripPollRef.current = setInterval(async () => {
            try {
                const res = await api.get(`/classify/search/${id}`);
                const st = res.data?.trip_status;
                if (st && st !== 'pending') {
                    // Resolved (complete or failed) — update the trip fields AND the
                    // Understudy Score. The backend recalculates the score on TRIP
                    // completion (the trip_band factor only applies once TRIP has a
                    // score), so the provisional number shown while pending must be
                    // replaced here — otherwise the badge stops pulsing but keeps
                    // displaying the pre-TRIP value.
                    setReport(prev => ({
                        ...prev,
                        trip_status: st,
                        trip_report_id: res.data.trip_report_id,
                        trip_score: res.data.trip_score,
                        trip_tier: res.data.trip_tier,
                        trip_tier_description: res.data.trip_tier_description,
                        result: {
                            ...prev.result,
                            understudy_score: res.data.result?.understudy_score || prev.result?.understudy_score
                        }
                    }));
                    clearInterval(tripPollRef.current);
                }
            } catch (e) {
                // Network hiccup — keep polling; will resolve or user can refresh
            }
        }, 5000);

        return () => {
            if (tripPollRef.current) clearInterval(tripPollRef.current);
        };
    }, [report?.trip_status, id]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/export/pdf/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `classify_report_${id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-500">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const cls = report.result?.classification || {};
  const ver = report.result?.verification || {};
  const op = report.result?.operational || {};
  const atm = report.result?.atmosphere || {};
  const acc = report.result?.accessibility || {};
  const park = report.result?.parking || {};
  const pay = report.result?.payment || {};
  const chain = report.result?.chain_info || {};
  const ic = report.result?.industry_codes || {};
  const ic2 = report.result?.secondary_industry_codes || null;
  const ic3 = report.result?.tertiary_industry_codes || null;
  const cc = report.result?.custom_codes || {};
  const uw = report.result?.uw_enrichment;
  const permits = report.result?.permits;
  // Real 911 calls-for-service. Searches run before this service existed have no
  // emergency_calls object — those fall through to the "not available" branch,
  // which is correct: their stored calls_911 number was the crime count relabeled.
  const ec = report.result?.uw_enrichment?.emergency_calls || null;
  const uscore = report.result?.understudy_score;
    const prop = report.result?.property_data;
    // LRO's subject is a BUILDING, not a business. Sections that describe a
    // business — classification codes, service options, a website, a Google
    // rating, an operating status — have no subject to describe, and rendering
    // them empty reads as a broken report rather than an inapplicable one.
    const isLro = report.input?.lob === 'lro';
  const img = report.result?.business_image;
  const liq = report.result?.liquor_license || {};

  // Handle both old string format and new object format for risk summary
  const rawSummary = report.result?.risk_summary;
  const riskSummary = rawSummary && typeof rawSummary === 'object'
    ? rawSummary
    : (rawSummary ? { sentences: rawSummary, bullets: null, identity: null } : null);

  const confColor = cls.confidence_level === 'high' ? 'confidence-high' : 
                    cls.confidence_level === 'medium' ? 'confidence-medium' : 'confidence-low';

  // RAG color helpers for score
  const getScoreColors = (score) => {
    if (score >= 90) return { header: 'bg-emerald-600', card: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', bullet: 'text-emerald-700' };
    if (score >= 75) return { header: 'bg-green-600', card: 'bg-green-50 border-green-200', text: 'text-green-800', bullet: 'text-green-700' };
    if (score >= 45) return { header: 'bg-yellow-500', card: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-800', bullet: 'text-yellow-700' };
    if (score >= 25) return { header: 'bg-orange-500', card: 'bg-orange-50 border-orange-200', text: 'text-orange-800', bullet: 'text-orange-700' };
    return { header: 'bg-red-600', card: 'bg-red-50 border-red-200', text: 'text-red-800', bullet: 'text-red-700' };
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Top Actions Bar */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate('/search')} className="text-teal hover:text-teal-600 font-medium text-sm">
          ← New Search
        </button>
        <div className="flex gap-2">
          <button 
            onClick={handleExportPDF} 
            disabled={exporting}
            className="btn-outline text-sm py-2 px-4"
          >
            {exporting ? 'Exporting...' : '📄 Export PDF'}
          </button>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 1: Business Header */}
      {/* ============================================ */}
      <div className="card mb-6">
        <div className="flex gap-5">
          {img?.url && (
            <img 
              src={img.url} 
              alt="Business" 
              className="w-36 h-28 rounded-lg object-cover flex-shrink-0 bg-gray-100"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="flex-1">
            <h1 className="font-heading text-2xl text-navy mb-1">
              {ver.google_name || report.input?.business_name}
            </h1>
            <div className="flex items-center gap-1 text-gray-500 text-sm">
              <span>📍</span>
              {ver.google_maps_url ? (
                <a href={ver.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">
                  {ver.google_address}
                </a>
              ) : (
                <span>{ver.google_address}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
              {ver.phone && <span>📞 {ver.phone}</span>}
              {ver.website && (
                <a href={ver.website} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">
                  🌐 Website
                </a>
              )}
              {!isLro && <span>Status: {ver.business_status === 'OPERATIONAL' ? '✅ Active' : ver.business_status || 'Unknown'}</span>}
              <span className="capitalize">LOB: {report.input?.lob?.replace('_', ' ')}</span>
            </div>
            {ver.description && (
              <p className="text-sm text-gray-500 mt-2 italic">{ver.description}</p>
            )}
            <div className="text-xs text-gray-300 mt-2">
              Report generated {new Date(report.createdAt).toLocaleString()}
            </div>
          </div>

          {/* Understudy Risk Score Badge */}
                  {uscore?.score != null && (
                      <div className="flex-shrink-0 text-center">
                          {/* While TRIP is running the score is PROVISIONAL: the trip_band
                              factor hasn't been applied yet. Pulse it so the number never
                              reads as final, then let the existing trip_status poll pick up
                              the recalculated score and settle it. */}
                          <div className={report.trip_status === 'pending' ? 'animate-pulse' : ''}>
                            <RiskBadge score={uscore.score} rag={uscore.rag} tierLabel={uscore.tier_label}
                                topRisks={uscore.top_risk_factors} favorables={uscore.favorable_factors} />
                          </div>
                          {report.trip_status === 'pending' ? (
                            <div className="text-[10px] text-teal-600 mt-1 animate-pulse">
                              ⏳ Provisional — awaiting TRIP
                            </div>
                          ) : (
                            <div className="text-[10px] text-gray-400 mt-1">
                                {uscore.property_included ? '📋 Operational + Property' : '📋 Operational only'}
                            </div>
                          )}
                      </div>
                  )}
        </div>
      </div>

      {/* ============================================ */}
      {/* UNDERSTUDY RISK SUMMARY CARD — v2          */}
      {/* ============================================ */}
      {riskSummary && uscore?.score != null && (() => {
        const sc = getScoreColors(uscore.score);
        const warnings = riskSummary.bullets?.warnings || [];
        const positives = riskSummary.bullets?.positives || [];

        return (
          <div className={`mb-6 rounded-xl overflow-hidden border ${sc.card}`}>
            {/* Header bar */}
            <div className={`px-4 py-2.5 flex items-center gap-2 ${sc.header}`}>
              <svg className="w-4 h-4 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-white text-sm font-semibold tracking-wide">Understudy Risk Summary</span>
            </div>

            <div className="px-5 py-4">
              {/* 2-sentence model output */}
              {riskSummary.sentences && (
                <p className={`text-sm leading-relaxed mb-4 ${sc.text}`}>
                  {riskSummary.sentences}
                </p>
              )}

              {/* Signal bullets — warnings then positives */}
              {(warnings.length > 0 || positives.length > 0) && (
                <div className="space-y-1.5 mb-4">
                  {warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-red-500 font-bold flex-shrink-0 mt-0.5">⚠</span>
                      <span className="text-red-700">{w}</span>
                    </div>
                  ))}
                  {positives.map((p, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-green-500 font-bold flex-shrink-0 mt-0.5">✓</span>
                      <span className="text-green-700">{p}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Score footer */}
              <div className={`flex items-center justify-between pt-3 border-t ${
                uscore.score >= 75 ? 'border-green-200' :
                uscore.score >= 45 ? 'border-yellow-200' : 'border-red-200'
              }`}>
                <p className="text-xs text-gray-400 italic">
                  Data analysis to support underwriter decision-making — not a binding recommendation.
                </p>
                <span className={`text-sm font-bold ml-4 flex-shrink-0 ${sc.text}`}>
                  {uscore.score}/100 — {uscore.tier_label}
                </span>
              </div>
            </div>
          </div>
              );
          })()}

          {/* ============================================ */}
          {/* TRIP PROFILE (add-on) — pulsing shield → resolved link */}
          {/* ============================================ */}
          {report.trip_status && report.trip_status !== 'none' && (
              <div className="card mb-6">
                  <h2 className="section-title">TRIP Profile</h2>
                  <p className="text-xs text-gray-400 mb-4">Location-based trafficking risk intelligence</p>

                  {report.trip_status === 'pending' && (
                      <div className="flex items-center gap-5">
                          <div className="trip-shield-pulsing flex-shrink-0">
                              <ScoreShield score={0} tier="ANALYZING" size="sm" />
                          </div>
                          <div>
                              <div className="font-semibold text-navy">Generating TRIP profile…</div>
                              <div className="text-sm text-gray-400 mt-1 leading-snug">
                                  Scanning the surrounding corridor, crime backdrop, and vice indicators. About 50 seconds.
                              </div>
                          </div>
                      </div>
                  )}

                  {report.trip_status === 'complete' && (
                      <div className="flex items-center gap-5">
                          <button
                              onClick={() => navigate(`/trip/report/${report.trip_report_id}`)}
                              className="hover:opacity-90 transition-opacity cursor-pointer flex-shrink-0"
                              title="View full TRIP report"
                          >
                              <ScoreShield score={report.trip_score ?? 0} tier={report.trip_tier || ''} size="sm" />
                          </button>
                          <div className="flex-1">
                              <div className="flex items-baseline gap-2">
                                  <span className="text-lg font-semibold text-navy capitalize">
                                      {(report.trip_tier || '').toLowerCase()}
                                  </span>
                                  <span className="text-xs text-gray-500">location risk</span>
                              </div>
                              <div className="text-sm text-gray-500 mt-1 mb-2 leading-snug">
                                  {report.trip_tier_description || 'Location risk assessment complete.'}
                              </div>
                              <button
                                  onClick={() => navigate(`/trip/report/${report.trip_report_id}`)}
                                  className="text-teal hover:text-teal-600 font-medium text-sm"
                              >
                                  View full TRIP report →
                              </button>
                          </div>
                      </div>
                  )}

                  {report.trip_status === 'failed' && (
                      <div className="text-sm text-gray-400 py-4">
                          ⚠️ TRIP profile could not be generated for this location. The classification
                          report above is unaffected.
                      </div>
                  )}
              </div>
          )}
          {/* ============================================ */}
          {/* LRO: Tenant Hazard, Building Profile, Roster */}
          {/* ============================================ */}
          {/* Two categorical bands rather than a 0-100 score. LRO scoring is
          structurally thin — storage had real premises factors, LRO has tenant
          mix plus location — so a number would look precise while carrying
          little information, and an LRO 78 would not mean what a storage 78
          means. The bands answer different questions and are kept separate: a
          pristine building full of bars is not the same risk as a tired building
          full of accountants, and one blended letter hides which you are looking
          at. */}
          {isLro && uw?.tenant_hazard && (() => {
              const th = uw.tenant_hazard;
              const te = uw.tenant_enrichment || {};
              const roster = uw.tenants || [];
              const assessed = th.assessed || [];

              // Insufficient sits OUTSIDE the ordinal scale — grey, not green — so it
              // reads as absent rather than favourable. Same principle as "? Not
              // stated" in the storage security card.
              const bandStyle = (band) => ({
                  'Severe': 'text-red-700',
                  'Elevated': 'text-orange-600',
                  'Moderate': 'text-yellow-600',
                  'Low': 'text-green-700',
                  'Insufficient data': 'text-gray-400'
              }[band] || 'text-gray-400');

              const bandCardStyle = (band) => ({
                  'Severe': 'bg-red-50 border-red-200',
                  'Elevated': 'bg-orange-50 border-orange-200',
                  'Moderate': 'bg-yellow-50 border-yellow-200',
                  'Low': 'bg-green-50 border-green-200',
                  'Insufficient data': 'bg-gray-50 border-gray-200'
              }[band] || 'bg-gray-50 border-gray-200');

              const propertyOn = prop?.available === true;

              return (
                  <>
                      {/* ── The two rating cards ─────────────────────────────── */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          <div className={`rounded-xl border p-4 ${bandCardStyle(th.rating)}`}>
                              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Tenant Hazard</div>
                              <div className={`text-2xl font-bold ${bandStyle(th.rating)}`}>{th.rating}</div>
                              {(th.reasons || []).length > 0 && (
                                  <div className="mt-2 space-y-0.5">
                                      {th.reasons.slice(0, 3).map((r, i) => (
                                          <div key={i} className="text-xs text-gray-600">{r}</div>
                                      ))}
                                  </div>
                              )}
                          </div>

                          {/* Shown even when property is OFF. An empty card is a nudge to
                  re-run with the add-on, and it reads as a gap in the ORDER
                  rather than a gap in the product. */}
                          {/* Same five bands as Tenant Hazard, so a reader comparing the two
                  cards compares like with like. Shown even when property is off —
                  an empty card reads as a gap in the ORDER rather than a gap in
                  the product, and is a nudge to re-run with the add-on. */}
              {(() => {
                const pe = uw.property_exposure;
                if (!propertyOn || !pe) {
                  return (
                    <div className="rounded-xl border p-4 bg-gray-50 border-gray-200">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Property Exposure</div>
                      <div className="text-2xl font-bold text-gray-400">Not requested</div>
                      <div className="text-xs text-gray-500 mt-2">
                        Add Property Data to include building characteristics, catastrophe grades and permit history.
                      </div>
                    </div>
                  );
                }
                return (
                  <div className={`rounded-xl border p-4 ${bandCardStyle(pe.rating)}`}>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Property Exposure</div>
                    <div className={`text-2xl font-bold ${bandStyle(pe.rating)}`}>{pe.rating}</div>
                    {(pe.reasons || []).length > 0 && (
                      <div className="mt-2 space-y-0.5">
                        {pe.reasons.map((r, i) => (
                          <div key={i} className="text-xs text-gray-600">{r}</div>
                        ))}
                      </div>
                    )}
                    {/* Regional perils are reported but never drive the band —
                        lightning, convection storm and mold all return F across
                        most of Florida, so they characterise the climate zone
                        rather than this building. Listed so nothing is hidden. */}
                    {(pe.regional || []).filter(p => p.grade === 'F' || p.grade === 'E').length > 0 && (
                      <div className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-200">
                        Regional: {pe.regional.filter(p => p.grade === 'F' || p.grade === 'E').map(p => p.peril + ' ' + p.grade).join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })()}
                      </div>

                      {/* ── Site imagery ─────────────────────────────────────── */}
                      {/* Placed before Building Profile so the reader sees the building
                before reading about it. Not gated on the property toggle — LRO's
                subject IS the building, so hiding it behind an add-on would hide
                the subject. */}
                      {uw.site_imagery?.available && (
                          <div className="card mb-6">
                              <h2 className="section-title">Site Imagery</h2>

                              {(uw.site_imagery.street_view || []).length > 0 ? (
                                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
                                      {uw.site_imagery.street_view.map((sv, i) => (
                                          <img
                                              key={i}
                                              src={sv.url}
                                              alt=""
                                              className="w-full h-36 object-cover rounded-lg border border-gray-200"
                                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                          />
                                      ))}
                                      {uw.site_imagery.satellite_url && (
                                          <img
                                              src={uw.site_imagery.satellite_url}
                                              alt=""
                                              className="w-full h-36 object-cover rounded-lg border border-gray-200"
                                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                          />
                                      )}
                                  </div>
                              ) : (
                                  // No pano — rural addresses and private roads both happen.
                                  // Satellite runs full width rather than leaving an empty tile;
                                  // absent imagery is absent, never a placeholder saying so.
                                  uw.site_imagery.satellite_url && (
                                      <img
                                          src={uw.site_imagery.satellite_url}
                                          alt=""
                                          className="w-full rounded-lg border border-gray-200 mt-4"
                                          style={{ maxHeight: '320px', objectFit: 'cover' }}
                                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                      />
                                  )
                              )}

                              {/* The capture date is not decoration. Across four probe
                    properties three were six to eight years old. A storefront
                    count does not change in that time, but tenant signage does. */}
                              <p className="text-xs text-gray-400 mt-2">
                                  {uw.site_imagery.capture_date
                                      ? `Street-level imagery captured ${uw.site_imagery.capture_date}. `
                                      : 'Street-level imagery unavailable for this location. '}
                                  Imagery © Google.
                              </p>
                          </div>
                      )}
                      {/* ── Building profile ─────────────────────────────────── */}
                      <div className="card mb-6">
                          <h2 className="section-title">Building Profile</h2>

                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 mt-4 text-sm">
                              <div className="flex justify-between">
                                  <span className="text-gray-500">Tenants identified</span>
                                  <span className="font-medium text-navy">{roster.length}</span>
                              </div>
                              {Object.entries(th.occupancy_mix || {})
                                  // Uncategorised is already reported as a hazard reason; leading
                                  // the occupancy mix with it says nothing about the building.
                                  .filter(([branch]) => branch !== 'uncategorised')
                                  .sort((a, b) => b[1] - a[1])
                                  .map(([branch, n]) => (
                                      <div key={branch} className="flex justify-between">
                                          <span className="text-gray-500 capitalize">{branch.replace(/_/g, ' ')}</span>
                                          <span className="font-medium text-navy">{n}</span>
                                      </div>
                                  ))}
                          </div>

                          {/* Google models some shopping centres as a place in their own
                  right and not others — where it does, that containment is a
                  better building key than any address string. Reported when
                  present, silent when not. */}
                          {te.containing_group && (
                              <div className="text-xs text-gray-400 mt-3">
                                  Google groups {te.containing_group.member_count} businesses at this location as one centre.
                              </div>
                          )}

                          {/* Distribution bar — proportion only, no claim about severity
                  weighting, which there is no loss data to support. */}
                          {assessed.length > 0 && (() => {
                              const c = th.counts || {};
                              const total = assessed.length;
                              const seg = (n, cls) => n > 0 ? <div key={cls} className={cls} style={{ width: `${(n / total) * 100}%` }} /> : null;
                              return (
                                  <div className="flex h-2 rounded-full overflow-hidden mt-4">
                                      {seg(c['Severe'], 'bg-red-500')}
                                      {seg(c['Elevated'], 'bg-orange-400')}
                                      {seg(c['Moderate'], 'bg-yellow-400')}
                                      {seg(c['Low'], 'bg-green-400')}
                                      {seg(c['Insufficient data'], 'bg-gray-300')}
                                  </div>
                              );
                          })()}
                      </div>

                      {/* ── Tenant roster ────────────────────────────────────── */}
                      <div className="card mb-6">
                          <h2 className="section-title">Tenants Identified</h2>

                          <div className="mt-4 divide-y divide-gray-100">
                              {assessed.map((a, i) => (
                                  <div key={i} className="flex items-baseline gap-3 py-2 text-sm">
                                      <span className={`w-28 shrink-0 font-medium ${bandStyle(a.band)}`}>
                                          {a.band === 'Insufficient data' ? 'Not categorised' : a.band}
                                      </span>
                                      <span className="flex-1 text-navy">
                                          {a.name}
                                          {a.suite && <span className="text-gray-400 text-xs ml-1">Ste {a.suite}</span>}
                                      </span>
                                      <span className="w-40 shrink-0 text-gray-500 text-xs capitalize">
                                          {(a.category || '').replace(/_/g, ' ')}
                                      </span>
                                      {/* Flags describe WHAT FIRED, never what it means. "Open past
                        10pm" is a fact; "elevated liability exposure" would be the
                        underwriter's conclusion. An unflagged row asserts nothing
                        — for most tenants no hours were ever matched, so a "no
                        hazards detected" label would claim more than is known. */}
                                      <span className="w-56 shrink-0 text-xs text-gray-500">
                                          {(a.flags || []).join(' · ')}
                                      </span>
                                  </div>
                              ))}
                          </div>

                          <p className="text-xs text-gray-400 italic mt-4">
                              Tenants identified from public business listings. This is not a rent roll — occupancy may include
                              tenants not listed publicly, and a listing may be out of date.
                              {te.meta?.not_operational > 0 && ` ${te.meta.not_operational} listing${te.meta.not_operational === 1 ? '' : 's'} reported as not currently operating.`}
                          </p>
                      </div>
                  </>
              );
          })()}

          {/* ============================================ */}
          {/* SECTION 2: Classification */}
          {/* ============================================ */}
          {/* Suppressed for LRO — the subject is a building, so there is no business
          activity to classify and the codes are deliberately null. */}
          {!isLro && (
              <div className="card mb-6">
                  <h2 className="section-title">Classification</h2>
        
        {(() => {
          const hasPropCodes = cc?.has_custom_codes === true;
          
          const ragColor = (conf) => {
            if (conf >= 90) return 'text-green-700 bg-green-50 border-green-200';
            if (conf >= 70) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
            return 'text-red-700 bg-red-50 border-red-200';
          };
          const ragDot = (conf) => conf >= 90 ? '🟢' : conf >= 70 ? '🟡' : '🔴';

          if (hasPropCodes) {
            const propTiers = [
              cc.primary ? { ...cc.primary, label: 'Primary' } : null,
              cc.secondary ? { ...cc.secondary, label: 'Alt 1' } : null,
              cc.tertiary ? { ...cc.tertiary, label: 'Alt 2' } : null,
            ].filter(Boolean);

            const codeTiers = [];
            if (cls.primary) codeTiers.push({ conf: cls.primary.confidence, codes: ic });
            if (cls.secondary) codeTiers.push({ conf: cls.secondary.confidence, codes: ic2 || {} });
            if (cls.tertiary) codeTiers.push({ conf: cls.tertiary.confidence, codes: ic3 || {} });

            const aggregate = (system) => {
              const map = {};
              codeTiers.forEach(t => {
                const code = t.codes?.[system] || 'N/A';
                const desc = t.codes?.[system + '_desc'] || '';
                if (code === 'N/A') return;
                if (!map[code]) map[code] = { code, desc, totalConf: 0 };
                map[code].totalConf = Math.max(map[code].totalConf, t.conf);
              });
              return Object.values(map).sort((a, b) => b.totalConf - a.totalConf);
            };

            const sicAgg = aggregate('sic');
            const naicsAgg = aggregate('naics');
            const aaisAgg = aggregate('aais');

            const CodeColumn = ({ title, items }) => (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase text-gray-400 mb-2 text-center">{title}</div>
                {items.map((item, i) => (
                  <div key={i} className={`border rounded-lg p-2 mb-1 text-center ${i === 0 ? ragColor(item.totalConf) : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                    <div className="text-xs">{i === 0 ? ragDot(item.totalConf) : ''} {Math.round(item.totalConf)}%</div>
                    <div className={`font-bold ${i === 0 ? 'text-lg' : 'text-sm'}`}>{item.code}</div>
                    <div className="text-xs mt-0.5 truncate" title={item.desc}>{item.desc}</div>
                  </div>
                ))}
              </div>
            );

            return (
              <>
                <div className={`border-2 rounded-lg p-5 mb-4 ${ragColor(propTiers[0]?.confidence || 0)}`}>
                  {propTiers.map((tier, i) => (
                    <div key={i} className={i === 0 ? 'text-center mb-3' : 'flex items-center justify-between px-4 py-2 border-t border-gray-200'}>
                      {i === 0 ? (
                        <>
                          <div className="text-xs font-bold uppercase mb-2">
                            {ragDot(tier.confidence)} Primary Classification
                          </div>
                          <div className="text-3xl font-bold">{tier.description}</div>
                          <div className="text-sm text-gray-500 mt-1">Code: {tier.code}</div>
                          <div className="text-sm font-semibold mt-1">{tier.confidence}% Confidence</div>
                        </>
                      ) : (
                        <>
                          <span className="text-xs font-bold uppercase text-gray-400 w-12">{tier.label}</span>
                          <span className="font-bold text-gray-600">{tier.code}</span>
                          <span className="text-sm text-gray-500 flex-1 text-center">{tier.description}</span>
                          <span className="text-sm text-gray-400">{tier.confidence}%</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  {sicAgg.length > 0 && <CodeColumn title="SIC (7-Digit)" items={sicAgg} />}
                  {naicsAgg.length > 0 && <CodeColumn title="NAICS" items={naicsAgg} />}
                 
                </div>
              </>
            );
          } else {
            const tiers = [];
            if (cls.primary) tiers.push({ conf: cls.primary.confidence, codes: ic });
            if (cls.secondary) tiers.push({ conf: cls.secondary.confidence, codes: ic2 || {} });
            if (cls.tertiary) tiers.push({ conf: cls.tertiary.confidence, codes: ic3 || {} });

            const aggregate = (system) => {
              const map = {};
              tiers.forEach(t => {
                const code = t.codes?.[system] || 'N/A';
                const desc = t.codes?.[system + '_desc'] || '';
                if (code === 'N/A') return;
                if (!map[code]) map[code] = { code, desc, totalConf: 0 };
                map[code].totalConf += t.conf;
              });
              return Object.values(map).sort((a, b) => b.totalConf - a.totalConf);
            };

            const sicAgg = aggregate('sic');
            const naicsAgg = aggregate('naics');
            const aaisAgg = aggregate('aais');

            const CodeColumn = ({ title, items }) => (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase text-gray-400 mb-2 text-center">{title}</div>
                {items.map((item, i) => (
                  <div key={i} className={`border rounded-lg p-2 mb-1 text-center ${i === 0 ? ragColor(item.totalConf) : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                    <div className="text-xs">{i === 0 ? ragDot(item.totalConf) : ''} {Math.round(item.totalConf)}%</div>
                    <div className={`font-bold ${i === 0 ? 'text-lg' : 'text-sm'}`}>{item.code}</div>
                    <div className="text-xs mt-0.5 truncate" title={item.desc}>{item.desc}</div>
                  </div>
                ))}
              </div>
            );

            return (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {sicAgg.length > 0 && <CodeColumn title="SIC (7-Digit)" items={sicAgg} />}
                  {naicsAgg.length > 0 && <CodeColumn title="NAICS" items={naicsAgg} />}
                  
                </div>
              </>
            );
          }
        })()}

        {chain?.is_chain && ['5000906', '5000707', '5000705'].includes(cls.primary?.code) && (
          <div className="bg-purple-50 rounded-lg p-3 mb-3 text-sm">
            <span className="font-semibold text-purple-700">🔗 Franchise Chain:</span>{' '}
            {chain.chain_name}
          </div>
        )}

        {liq.found && (
          <div className="bg-amber-50 rounded-lg p-3 mb-3 text-sm">
            <span className="font-semibold text-amber-700">🍺 Liquor License Verified</span>
            {liq.license_type && <span> — {liq.license_type}</span>}
            {liq.has_full_bar && <span className="ml-2 text-amber-600 font-bold"> — Full Bar</span>}
            {liq.matched_name && <div className="text-xs text-amber-600 mt-1">Licensed to: {liq.matched_name} ({liq.match_method})</div>}
          </div>
        )}

        {cls.rationale && (
        <p className="text-sm text-gray-500 mt-2">📋 {cls.rationale}</p>
              )}
          </div>
      )}

      {/* ============================================ */}
      {/* SECTION 3: Operational Profile — suppressed for hotels (rating shown in Review Intelligence) */}
      {/* ============================================ */}
      {report.input?.lob !== 'hotel' && !isLro && (
      <div className="card mb-6">
        <h2 className="section-title">Operational Profile</h2>
        
        <BooleanGrid title="Service Options" items={[
          { label: 'Dine-In', value: op.dine_in },
          { label: 'Takeout', value: op.takeout },
          { label: 'Delivery', value: op.delivery },
          { label: 'Curbside', value: op.curbside_pickup },
          { label: 'Reservable', value: op.reservable },
        ]} />

        <BooleanGrid title="Food Service" items={[
          { label: 'Breakfast', value: op.serves_breakfast },
          { label: 'Lunch', value: op.serves_lunch },
          { label: 'Dinner', value: op.serves_dinner },
          { label: 'Brunch', value: op.serves_brunch },
          { label: 'Coffee', value: op.serves_coffee },
          { label: 'Dessert', value: op.serves_dessert },
          { label: 'Vegetarian', value: op.serves_vegetarian },
        ]} />

        <BooleanGrid title="Alcohol" items={[
          { label: 'Beer', value: op.serves_beer },
          { label: 'Wine', value: op.serves_wine },
          { label: 'Cocktails', value: op.serves_cocktails },
        ]} />

        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-4">
          {op.rating && <span>⭐ {op.rating}/5 ({op.review_count} reviews)</span>}
          {op.price_level && <span>💰 {op.price_level}</span>}
          {op.price_range_min != null && op.price_range_max != null && (
            <span>💵 ${op.price_range_min} – ${op.price_range_max}</span>
          )}
        </div>

        {op.hours?.descriptions && (
          <div className="mt-4 text-sm text-gray-500">
            <div className="font-semibold text-navy mb-1">
              Hours
              {op.hours.open_now != null && (
                <span className={`ml-2 text-xs font-bold ${op.hours.open_now ? 'text-green-600' : 'text-red-500'}`}>
                  {op.hours.open_now ? '● Open Now' : '● Closed'}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
              {op.hours.descriptions.map((h, i) => <div key={i}>{h}</div>)}
            </div>
          </div>
        )}
      </div>
      )}

      {/* ============================================ */}
      {/* HOTEL HERO: Location map + Nearby businesses (Hotels only) */}
      {/* ============================================ */}
      {report.input?.lob === 'hotel' && (report.result?.location_map || uw?.nearby) && (
        <div className="card mb-6">
          <h2 className="section-title">📍 Location & Surroundings</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">

            {/* Left: locator map */}
            <div>
              {report.result?.location_map ? (
                <img
                  src={report.result.location_map}
                  alt="Property location map"
                  className="w-full rounded-lg border border-gray-200"
                  style={{ aspectRatio: '3 / 2', objectFit: 'cover' }}
                />
              ) : (
                <div className="w-full rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-400 text-sm" style={{ aspectRatio: '3 / 2' }}>
                  Map unavailable
                </div>
              )}
              {ver?.google_address && (
                <p className="text-xs text-gray-500 mt-2">📌 {ver.google_address}</p>
              )}
            </div>

            {/* Right: nearby — closest 5 (factual) + risky-neighbor flags */}
            <div>
              {uw?.hotel_nearby?.available ? (
                <>
                  {/* Risky neighbors — flagged first, they're the signal */}
                  {uw.hotel_nearby.has_risky_neighbor && (
                    <div className="mb-4">
                      <h3 className="font-semibold text-red-700 text-sm mb-2">⚠ Risky Neighbors (within 1mi)</h3>
                      <div className="space-y-2">
                        {uw.hotel_nearby.risky_neighbors.map((r, i) => (
                          <div key={i} className="text-xs px-3 py-2 rounded-lg font-medium bg-red-50 text-red-700 border border-red-200">
                            🚩 {r.label} — {r.name}
                            {r.distance_str && <span className="ml-1 opacity-70">({r.distance_str})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Closest 5 — factual context, no risk judgment */}
                  <h3 className="font-semibold text-navy text-sm mb-2">Closest Businesses</h3>
                  {uw.hotel_nearby.closest_businesses?.length > 0 ? (
                    <div className="space-y-1">
                      {uw.hotel_nearby.closest_businesses.map((b, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{b.name}</span>
                          <span className="text-gray-400 text-xs ml-2">{b.distance_str}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400">No nearby businesses found</div>
                  )}

                  {!uw.hotel_nearby.has_risky_neighbor && (
                    <p className="text-xs text-gray-400 mt-3">
                      ✓ No casino or adult entertainment venue within 1mi.
                    </p>
                  )}
                </>
              ) : (
                <div className="text-sm text-gray-400">Nearby business data unavailable</div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* HOTEL: Corridor card (declination-grade — confirmed/inferred/unconfirmed) */}
      {/* ============================================ */}
      {report.input?.lob === 'hotel' && uw?.hotel_intelligence?.corridor && (() => {
        const c = uw.hotel_intelligence.corridor;
        const status = c.corridor_status; // 'confirmed' | 'inferred' | 'unconfirmed'
        const type = c.corridor_type;     // 'interior' | 'exterior' | 'unconfirmed'

        // Status styling: confirmed = green (observed), inferred = amber (verify), unconfirmed = gray
        const statusStyle =
          status === 'confirmed' ? 'bg-green-100 text-green-700 border-green-200' :
          status === 'inferred'  ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                   'bg-gray-100 text-gray-600 border-gray-200';
        const statusLabel =
          status === 'confirmed' ? 'Confirmed' :
          status === 'inferred'  ? 'Inferred — verify' :
                                   'Unconfirmed — manual review';

        // Type label + whether exterior (the risk-relevant case)
        const typeLabel =
          type === 'exterior' ? 'Exterior Corridor' :
          type === 'interior' ? 'Interior Corridor' :
                                'Corridor Type Undetermined';
        const isExterior = type === 'exterior';

        const sourceLabel =
          c.corridor_source === 'vision'        ? 'Confirmed from property photos' :
          c.corridor_source === 'text_evidence' ? 'Confirmed from listing/review evidence' :
          c.corridor_source === 'chain_prior'   ? 'Inferred from brand standard' :
                                                  'No confirming source';

        return (
          <div className="card mb-6">
            <h2 className="section-title">🚪 Corridor Configuration</h2>
            <div className="mt-4">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className={`text-lg font-bold ${isExterior ? 'text-orange-600' : 'text-navy'}`}>
                  {isExterior && '⚠️ '}{typeLabel}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${statusStyle}`}>
                  {statusLabel}
                </span>
                {c.corridor_confidence != null && (
                  <span className="text-xs text-gray-400">{c.corridor_confidence}% confidence</span>
                )}
              </div>

              <p className="text-xs text-gray-500 mb-2">{sourceLabel}</p>

              {c.corridor_cues && (
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                  {c.corridor_cues}
                </p>
              )}

              {c.floors_estimate != null && (
                <p className="text-xs text-gray-500 mt-2">Estimated floors: {c.floors_estimate}</p>
              )}

              {isExterior && (
                <p className="text-xs text-orange-600 mt-3">
                  ⚠️ Exterior-corridor properties may fall outside appetite for some carriers.
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {/* ============================================ */}
      {/* HOTEL: Amenities (risk-flagged: pool/bar/rooftop/restaurant) */}
      {/* ============================================ */}
      {report.input?.lob === 'hotel' && uw?.hotel_intelligence?.amenities && (() => {
        const a = uw.hotel_intelligence.amenities;
        const g = uw.hotel_intelligence.google_data || {};
        return (
          <div className="card mb-6">
            <h2 className="section-title">🏨 Amenities & Features</h2>
            <p className="text-xs text-gray-400 mb-4">
              ⚠ = features that may affect appetite or add exposure. Based on Google + public data — verify.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
              {/* Left column: risk-relevant / exposure amenities */}
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Exposure-Relevant</div>
                <div className="space-y-2">
                  <SecurityRow label="Pool" detected={a.pool} risk={true} />
                  <SecurityRow label="Bar / Lounge On-Site" detected={a.bar_lounge_onsite} risk={true} />
                  <SecurityRow label="Serves Alcohol" detected={a.serves_alcohol} risk={true} />
                  <SecurityRow label="Restaurant On-Site" detected={a.restaurant_onsite} risk={true} />
                  <SecurityRow label="Attached to Restaurant" detected={a.attached_to_restaurant} risk={true} />
                  <SecurityRow label="Live Music" detected={a.live_music} risk={true} />
                </div>
              </div>

              {/* Right column: standard amenities (informational) */}
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Standard</div>
                <div className="space-y-2">
                  <SecurityRow label="Free Breakfast" detected={a.free_breakfast} risk={false} />
                  <SecurityRow label="Hot Cooked Breakfast" detected={a.hot_cooked_breakfast} risk={false} />
                  <SecurityRow label="Fitness Center" detected={a.fitness_center} risk={false} />
                  <SecurityRow label="Shuttle Service" detected={a.shuttle_service} risk={false} />
                  <SecurityRow label="Extended Stay" detected={a.extended_stay} risk={false} />
                  <SecurityRow label="Pet Friendly" detected={a.pet_friendly} risk={false} />
                  <SecurityRow label="Happy Hour" detected={a.happy_hour} risk={false} />
                </div>
              </div>
            </div>

            {/* Property facts row */}
            {(a.room_count_estimate != null || g.ev_charge_options || (g.containing_places && g.containing_places.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {a.room_count_estimate != null && (
                  <span className="text-gray-600"><span className="text-gray-400">Rooms (est.):</span> {a.room_count_estimate}</span>
                )}
                {g.ev_charge_options && (
                  <span className="text-gray-600">
                    <span className="text-gray-400">EV Charging:</span> {g.ev_charge_options.connector_count ? `${g.ev_charge_options.connector_count} connector(s)` : 'Available'}
                  </span>
                )}
                {g.containing_places && g.containing_places.length > 0 && (
                  <span className="text-gray-600">
                    <span className="text-gray-400">Located Within:</span> {g.containing_places.map(cp => cp.display_name).filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ============================================ */}
      {/* HOTEL: Security & Access (honest Unknown treatment) */}
      {/* ============================================ */}
      {report.input?.lob === 'hotel' && uw?.hotel_intelligence?.security && (() => {
        const s = uw.hotel_intelligence.security;
        // Explicit tri-state row: Yes / No / Unknown — security gaps shown honestly, not hidden.
        const SecAccessRow = ({ label, val }) => {
          let style, text;
          if (val === true)       { style = 'text-green-700'; text = '✓ Yes'; }
          else if (val === false) { style = 'text-gray-400';  text = '✗ No'; }
          else                    { style = 'text-gray-400 italic'; text = 'Unknown'; }
          return (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">{label}</span>
              <span className={`font-medium ${style}`}>{text}</span>
            </div>
          );
        };

        const parkingLabel = s.parking_type
          ? s.parking_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          : (s.parking_summary || null);

        return (
          <div className="card mb-6">
            <h2 className="section-title">🔐 Security & Access</h2>
            <p className="text-xs text-gray-400 mb-4">
              Based on available public data — verify with property directly. "Unknown" indicates the detail was not publicly available.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
              <div className="space-y-2">
                <SecAccessRow label="24-Hour Front Desk" val={s.front_desk_24hr} />
                <SecAccessRow label="Security Cameras (Common Areas)" val={s.cameras_common_areas} />
                <SecAccessRow label="Keycard Floor Access" val={s.keycard_floor_access} />
              </div>
              <div className="space-y-2">
                <SecAccessRow label="On-Site Security" val={s.on_site_security} />
                <SecAccessRow label="Parking Well-Lit" val={s.parking_lit} />
                {parkingLabel && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Parking Type</span>
                    <span className="font-medium text-gray-600">{parkingLabel}</span>
                  </div>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-400 mt-4">
              ⚠ Based on publicly available data. Manual verification recommended.
            </p>
          </div>
        );
      })()}

      {/* ============================================ */}
      {/* HOTEL: Review Intelligence (replaces generic sentiment for hotels) */}
      {/* ============================================ */}
      {report.input?.lob === 'hotel' && (uw?.hotel_intelligence?.review_intelligence || uw?.hotel_intelligence?.google_data) && (() => {
        const ri = uw.hotel_intelligence.review_intelligence || {};
        const g = uw.hotel_intelligence.google_data || {};
        const safety = ri.safety_mentions || [];
        const themes = ri.recurring_themes || [];
        const sentimentStyle =
          ri.overall_sentiment === 'positive' ? 'bg-green-100 text-green-700' :
          ri.overall_sentiment === 'negative' ? 'bg-red-100 text-red-700' :
          ri.overall_sentiment === 'mixed'    ? 'bg-yellow-100 text-yellow-700' :
                                                 'bg-gray-100 text-gray-600';
        return (
          <div className="card mb-6">
            <h2 className="section-title">💬 Review Intelligence</h2>

            {/* Rating + sentiment header */}
            <div className="flex flex-wrap items-center gap-3 mt-4 mb-4">
              {g.rating != null && (
                <span className="text-lg font-bold text-navy">⭐ {g.rating}/5</span>
              )}
              {g.review_count != null && (
                <span className="text-sm text-gray-400">({g.review_count} reviews)</span>
              )}
              {ri.overall_sentiment && (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${sentimentStyle}`}>
                  {ri.overall_sentiment.charAt(0).toUpperCase() + ri.overall_sentiment.slice(1)}
                </span>
              )}
            </div>

            {/* Google AI review summary (rich prose) */}
            {g.review_summary && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Review Summary</div>
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 whitespace-pre-line">
                  {g.review_summary}
                </p>
              </div>
            )}

            {/* Google AI place overview */}
            {g.generative_summary && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Property Overview</div>
                <p className="text-sm text-gray-600">{g.generative_summary}</p>
              </div>
            )}

            {/* Recurring themes */}
            {themes.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Recurring Themes</div>
                <div className="flex flex-wrap gap-2">
                  {themes.map((t, i) => (
                    <span key={i} className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Safety signals — prominent, serious content */}
            {safety.length > 0 && (
              <div className="mt-4 pt-4 border-t border-red-100">
                <div className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">⚠ Safety Signals from Reviews</div>
                <div className="space-y-1">
                  {safety.map((s, i) => (
                    <div key={i} className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
                      🚩 {s}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ============================================ */}
      {/* SECTION 3b: Security Profile (Self Storage only) */}
      {/* ============================================ */}
      {report.input?.lob === 'self_storage' && uw?.storage_indicators && (
              <div className="card mb-6">
                  <h2 className="section-title">🔒 Security Profile</h2>
                  <p className="text-xs text-gray-400 mb-4">
                      Based on available public data — verify with facility directly
                      
                  </p>

                  {/* Security Level Badge */}
                  {uw.security_profile?.security_level && (
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold mb-4 ${uw.security_profile.security_level === 'High' ? 'bg-green-100 text-green-700' :
                              uw.security_profile.security_level === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
                                  uw.security_profile.security_level === 'Basic' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700'
                          }`}>
                          Security Level: {uw.security_profile.security_level}
                      </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                      {/* Security Features */}
                      <div>
                          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Security</div>
                          <div className="space-y-2">
                              <SecurityRow label="Gated Access" detected={uw.storage_indicators.gated_access} risk={false} />
                              <SecurityRow label="Keypad / PIN Access" detected={uw.storage_indicators.keypad_access} risk={false} />
                              <SecurityRow label="Security Cameras" detected={uw.storage_indicators.security_cameras} risk={false} />
                              <SecurityRow label="On-Site Manager" detected={uw.storage_indicators.onsite_manager} risk={false} />
                              <SecurityRow label="Individual Unit Alarms" detected={uw.storage_indicators.individual_alarms} risk={false} />
                              {uw.storage_vision?.detail?.fence_type && uw.storage_vision.detail.fence_type !== 'none_visible' && (
                                  <div className="flex items-center justify-between text-sm">
                                      <span className="text-gray-500">Perimeter Fence</span>
                                      <span className="font-medium text-gray-700">
                                          {{ chain_link: 'Chain link', solid_wall: 'Solid wall', wrought_iron: 'Wrought iron' }[uw.storage_vision.detail.fence_type] || uw.storage_vision.detail.fence_type}
                                      </span>
                                  </div>
                              )}
                              {uw.storage_vision?.detail?.fence_height_est_ft != null && (
                                  <div className="flex items-center justify-between text-sm">
                                      <span className="text-gray-500">Fence Height</span>
                                      <span className="font-medium text-gray-700">~{uw.storage_vision.detail.fence_height_est_ft} ft</span>
                                  </div>
                              )}
                          </div>
                      </div>

                      {/* Access & Units */}
                      <div>
                          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Access & Unit Type</div>
                          <div className="space-y-2">
                              <SecurityRow label="24-Hour Access" detected={uw.storage_indicators.open_24hrs} risk={true} />
                              <SecurityRow label="Climate Controlled" detected={uw.storage_indicators.climate_controlled} risk={false} />
                              <SecurityRow label="Drive-Up / Outdoor Units" detected={uw.storage_indicators.outdoor_units} risk={true} />
                              <SecurityRow label="Indoor Units" detected={uw.storage_indicators.indoor_units} risk={false} />
                              
                              <SecurityRow label="RV / Boat / Vehicle Storage" detected={uw.storage_indicators.rv_boat_storage} risk={true} />
                              {uw.storage_vision?.detail?.building_count != null && (
                                  <div className="flex items-center justify-between text-sm">
                                      <span className="text-gray-500">Buildings on Site</span>
                                      <span className="font-medium text-gray-700">{uw.storage_vision.detail.building_count}</span>
                                  </div>
                              )}
                              {uw.storage_vision?.detail?.unit_row_count != null && (
                                  <div className="flex items-center justify-between text-sm">
                                      <span className="text-gray-500">Unit Rows Visible</span>
                                      <span className="font-medium text-gray-700">{uw.storage_vision.detail.unit_row_count}</span>
                                  </div>
                              )}
                          </div>
                      </div>

                      {/* Risk Transfer */}
                      <div>
                          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Risk Transfer & Operations</div>
                          <div className="space-y-2">
                              <SecurityRow label="Tenant Insurance Required" detected={uw.storage_indicators.tenant_insurance} risk={false} />
                              <SecurityRow label="Truck Rental Available" detected={uw.storage_indicators.truck_rental} risk={true} />
                              <SecurityRow label="Moving Supplies (Retail)" detected={uw.storage_indicators.moving_supplies} risk={false} />
                          </div>
                      </div>
                  </div>

                  <p className="text-xs text-gray-400 mt-4 italic">
                      ⚠ Based on publicly available data. Manual verification recommended.
                  </p>
              </div>
          )}



      {/* ============================================ */}
      {/* SECTION 4: Atmosphere */}
      {/* ============================================ */}
      {report.input?.lob !== 'hotel' && (
      <SectionIfAnyData data={atm}>
        <div className="card mb-6">
          <h2 className="section-title">Atmosphere</h2>
          <BooleanGrid items={[
            { label: 'Outdoor Seating', value: atm.outdoor_seating },
            { label: 'Live Music', value: atm.live_music },
            { label: 'Good for Children', value: atm.good_for_children },
            { label: 'Good for Groups', value: atm.good_for_groups },
            { label: 'Good for Sports', value: atm.good_for_watching_sports },
            { label: 'Kids Menu', value: atm.menu_for_children },
            { label: 'Dogs Allowed', value: atm.allows_dogs },
            { label: 'Has Restroom', value: atm.has_restroom },
          ]} />
        </div>
      </SectionIfAnyData>
      )}

      {/* ============================================ */}
      {/* SECTION 5: Accessibility, Parking & Payment — suppressed for hotels */}
      {/* ============================================ */}
      {report.input?.lob !== 'hotel' && (
      <SectionIfAnyData data={{ ...acc, ...park, ...pay }}>
        <div className="card mb-6">
          <h2 className="section-title">Accessibility, Parking & Payment</h2>
          
          <SectionIfAnyData data={acc}>
            <BooleanGrid title="Wheelchair Accessibility" items={[
              { label: 'Parking', value: acc.wheelchair_parking },
              { label: 'Entrance', value: acc.wheelchair_entrance },
              { label: 'Restroom', value: acc.wheelchair_restroom },
              { label: 'Seating', value: acc.wheelchair_seating },
            ]} />
          </SectionIfAnyData>

          <SectionIfAnyData data={park}>
            <BooleanGrid title="Parking" items={[
              { label: 'Free Lot', value: park.free_parking_lot },
              { label: 'Paid Lot', value: park.paid_parking_lot },
              { label: 'Free Street', value: park.free_street_parking },
              { label: 'Paid Street', value: park.paid_street_parking },
              { label: 'Valet', value: park.valet_parking },
              { label: 'Free Garage', value: park.free_garage_parking },
              { label: 'Paid Garage', value: park.paid_garage_parking },
            ]} />
          </SectionIfAnyData>

          <SectionIfAnyData data={pay}>
            <BooleanGrid title="Payment" items={[
              { label: 'Credit Cards', value: pay.accepts_credit_cards },
              { label: 'Debit Cards', value: pay.accepts_debit_cards },
              { label: 'Cash Only', value: pay.accepts_cash_only },
              { label: 'NFC / Tap', value: pay.accepts_nfc },
            ]} />
          </SectionIfAnyData>
        </div>
      </SectionIfAnyData>
      )}

      {/* ============================================ */}
      {/* SECTION 6: Risk & Location Intelligence */}
      {/* ============================================ */}
      {uw && (
        <div className="card mb-6">
          <h2 className="section-title">Risk & Location Intelligence</h2>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Crime Data */}
            <div>
              <h3 className="font-semibold text-navy text-sm mb-2">Crime Data</h3>
              {(uw.crime?.coverage === 'not_covered' || uw.crime?.coverage === 'insufficient' || uw.crime?.coverage === 'error' || uw.crime?.coverage === 'not_configured') ? (
                <div className="text-sm text-gray-400 italic">
                  {uw.crime?.coverage === 'not_covered'
                    ? 'Crime data not available — location outside coverage area.'
                    : uw.crime?.coverage === 'insufficient'
                    // Distinct from "not covered": the city IS in coverage, but this
                    // property sits at the jurisdiction edge, so the few incidents we
                    // get characterize almost nothing. Saying "not covered" here would
                    // contradict what the underwriter sees in the coverage list.
                    ? 'Insufficient crime data for this location — not scored.'
                    : uw.crime?.coverage === 'error'
                    ? 'Crime data temporarily unavailable.'
                    : 'Crime data not available.'}
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <DataRow label="Crime Score" value={uw.crime?.score != null ? `${uw.crime.score}/100` : null} />
                    {/* EXACT 12-month total from /v2/incidents/stats. This previously  */}
                    {/* showed whatever 5 pages of /v2/crime-incidents returned — capped */}
                    {/* at ~500 and labeled "12mo" regardless. The Chicago Loop read 500 */}
                    {/* against a real 5,242.                                            */}
                    {uw.crime?.total_incidents_12mo != null && (
                      <DataRow label="Total Incidents (12mo)" value={uw.crime.total_incidents_12mo.toLocaleString()} />
                    )}
                    {/* Violent Incidents / Property Incidents / Violent Crime Rate are */}
                    {/* gone: they came from the Person/Property/Society axis over a     */}
                    {/* truncated sample. Replaced by the offense table below, which     */}
                    {/* carries Crimeometer's own labels and exact counts.               */}
                    <DataRow label="Risk Level" value={uw.crime?.risk_level || null}
                      valueClass={uw.crime?.risk_level === 'Low' ? 'text-green-600' : uw.crime?.risk_level === 'High' ? 'text-red-600' : 'text-yellow-600'} />
                  </div>

                  {/* -------------------------------------------------------- */}
                  {/* Offense breakdown — Crimeometer's types, exact counts.   */}
                  {/* -------------------------------------------------------- */}
                  {/* ALPHABETICAL, deliberately. Sorting by count would imply  */}
                  {/* frequency = importance, which is false and misleading:    */}
                  {/* 923 Shoplifting above 39 Sexual Assault tells an          */}
                  {/* underwriter exactly the wrong thing. Alphabetical makes   */}
                  {/* no claim — it's a lookup table, stable across every       */}
                  {/* report, and new offense types slot in automatically.      */}
                  {/*                                                           */}
                  {/* No grouping: these are Crimeometer's NIBRS sub-types      */}
                  {/* passed straight through. Any bucketing would be our       */}
                  {/* judgment on their data, needing maintenance as their      */}
                  {/* vocabulary grows.                                         */}
                  {Array.isArray(uw.crime?.incident_types) && uw.crime.incident_types.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <h4 className="font-semibold text-navy text-xs mb-2">Incidents by Offense Type</h4>
                      {/* CSS columns rather than a JS breakpoint: the browser   */}
                      {/* balances by height and reflows on resize, so no        */}
                      {/* threshold to invent. break-inside stops a row splitting */}
                      {/* across the column boundary.                            */}
                      <div style={{ columns: 2, columnGap: '1.5rem' }}>
                        {[...uw.crime.incident_types]
                          .sort((a, b) => String(a.type).localeCompare(String(b.type)))
                          .map(t => (
                            <div key={t.type} className="flex justify-between text-sm gap-3" style={{ breakInside: 'avoid' }}>
                              <span className="text-gray-500">{t.type}</span>
                              <span className="font-medium text-navy">{Number(t.count).toLocaleString()}</span>
                            </div>
                          ))}
                      </div>
                      <div className="text-xs text-gray-400 italic mt-2">
                        Offense types with at least one incident in the last 12 months within {uw.crime?.radius_mi ?? 0.5}mi. Counts are exact.
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ---------------------------------------------------------- */}
              {/* 911 Calls for Service                                      */}
              {/* ---------------------------------------------------------- */}
              {/* Deliberately OUTSIDE the crime coverage branch above. 911 and   */}
              {/* crime are different datasets with different footprints (~80     */}
              {/* cities vs ~600 jurisdictions) — a property can have 911         */}
              {/* coverage and no crime coverage, or the reverse. Nesting this    */}
              {/* inside the crime else-branch (as it was) made 911 invisible     */}
              {/* wherever crime was uncovered, and framed it as a crime          */}
              {/* sub-metric, which it is not.                                    */}
              {/*                                                                 */}
              {/* The value here previously read the crime incident count wearing */}
              {/* a 911 label. Now it reads real calls-for-service data.          */}
              <div className="mt-4 pt-3 border-t border-gray-100">
                <h3 className="font-semibold text-navy text-sm mb-2">911 Calls for Service</h3>
                {ec?.available === true && ec.coverage === 'covered' ? (
                  <>
                    <div className="space-y-2">
                      <DataRow label="Total Calls (12mo)" value={
                        typeof ec.total_calls_12mo === 'number' ? ec.total_calls_12mo.toLocaleString() : null
                      } />
                    </div>
                    {/* ------------------------------------------------------------ */}
                    {/* CATEGORY BREAKDOWN — intentionally not rendered.             */}
                    {/* ------------------------------------------------------------ */}
                    {/* The breakdown rows are disabled at the service layer          */}
                    {/* (emergencyCalls.js returns breakdown: null); see that file's  */}
                    {/* rationale block. Summary: no aggregate endpoint for 911 (the  */}
                    {/* 403s are ambiguous and the question is open with Crimeometer), */}
                    {/* exact counts would cost ~80 page fetches on the dense cities   */}
                    {/* where most covered properties sit, and 911 coverage is a       */}
                    {/* strict subset of crime coverage — so every report that could   */}
                    {/* show a 911 breakdown ALREADY shows an exact crime breakdown    */}
                    {/* from /v2/incidents/stats. An estimated category table beside   */}
                    {/* an exact one is duplicative noise.                             */}
                    {/*                                                                */}
                    {/* The total stays: it's exact and additive (Chicago 8,059 calls  */}
                    {/* vs 5,239 crime incidents — calls that never became reports).   */}
                    {/*                                                                */}
                    {/* CALL_CATEGORIES + fmtCalls are retained for the same reason    */}
                    {/* the service keeps its sampling code: if Crimeometer confirms   */}
                    {/* an aggregate endpoint, this comes back with EXACT counts.      */}
                    {/*                                                                */}
                    {/* {ec.breakdown && CALL_CATEGORIES.map(c => (                    */}
                    {/*   <DataRow key={c.key} label={c.label}                         */}
                    {/*     value={fmtCalls(ec.breakdown[c.key], ec.breakdown_method)} */}
                    {/*     valueClass={c.valueClass} />                               */}
                    {/* ))}                                                            */}
                    <div className="text-xs text-gray-400 italic mt-2">
                      Within {ec.radius_mi ?? 0.5}mi. Reporting lag varies by jurisdiction — recent calls may not yet appear.
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-400 italic">
                    {ec?.coverage === 'not_covered'
                      ? '911 call data not available — location outside coverage area.'
                      : ec?.coverage === 'error'
                      ? '911 call data temporarily unavailable.'
                      : ec?.coverage === 'not_configured'
                      ? '911 call data not available.'
                      /* No emergency_calls object at all — a search run before this */
                      /* service existed. Those records carry a stored 911 value,    */
                      /* but it was the crime count relabeled, so show nothing       */
                      /* rather than resurface a number that was never real.         */
                      : '911 call data not available for this search.'}
                  </div>
                )}
              </div>
            </div>

            {/* Sentiment — hidden for hotels (replaced by Review Intelligence section) */}
            {report.input?.lob !== 'hotel' && !isLro && (
            <div>
              <h3 className="font-semibold text-navy text-sm mb-2">Sentiment Analysis</h3>
              <div className="space-y-2">
                <DataRow label="Google Rating" value={uw.sentiment?.google_rating ? `${uw.sentiment.google_rating}/5` : null} />
                <DataRow label="Reviews" value={uw.sentiment?.google_review_count || null} />
                <DataRow label="Summary" value={uw.sentiment?.summary !== 'N/A' ? uw.sentiment?.summary : null} />
              </div>
            </div>
            )}
          </div>

          {/* Risk Indicators */}
          {uw.risk_indicators && Object.values(uw.risk_indicators).some(v => v === true) && (
            <div className="mt-6">
              <h3 className="font-semibold text-navy text-sm mb-3">Risk Indicators</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(uw.risk_indicators)
                  .filter(([_, val]) => val === true)
                  .map(([key]) => (
                  <div key={key} className="text-xs px-3 py-2 rounded-lg font-medium bg-red-50 text-red-700 border border-red-200">
                    ⚠️ {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nearby Businesses — v2 with distance. Hidden for hotels (shown in Location hero). */}
          {report.input?.lob !== 'hotel' && uw.nearby && (
            <div className="mt-6">
              <h3 className="font-semibold text-navy text-sm mb-3">
                Nearby Businesses 
                <span className="font-normal text-gray-400 ml-1">
                  ({uw.nearby.total_neighbors || 0} within 1mi)
                </span>
              </h3>
              {uw.nearby.sufficient_data ? (
                <div>
                  {/* Summary signal badge */}
                  {(() => {
                    const hasHigh = uw.nearby.high_negatives?.length > 0;
                    const hasMild = uw.nearby.mild_negatives?.length > 0;
                    const hasLateNight = uw.nearby.late_night_businesses?.length >= 2;
                    const posCount = uw.nearby.positives?.length || 0;
                    
                    if (hasHigh) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-red-50 text-red-700 border border-red-200 inline-block mb-3">
                        🚩 High-risk businesses in vicinity
                      </div>
                    );
                    if (hasMild && hasLateNight) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-orange-50 text-orange-700 border border-orange-200 inline-block mb-3">
                        ⚠️ Late-night district with negative indicators
                      </div>
                    );
                    if (hasMild) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 inline-block mb-3">
                        ⚠️ Some negative businesses nearby
                      </div>
                    );
                    if (hasLateNight) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-orange-50 text-orange-700 border border-orange-200 inline-block mb-3">
                        🌙 Late-night district — {uw.nearby.late_night_businesses.length} bars/clubs nearby
                      </div>
                    );
                    if (posCount > 0) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-green-50 text-green-700 border border-green-200 inline-block mb-3">
                        ✓ Clean area — {posCount} professional/commercial neighbor{posCount > 1 ? 's' : ''}
                      </div>
                    );
                    return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-gray-50 text-gray-500 border border-gray-200 inline-block mb-3">
                        Neutral area — no notable indicators
                      </div>
                    );
                  })()}

                  {/* Notable neighbors with distance — v2 */}
                  {uw.nearby.notable?.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                      {uw.nearby.notable.map((n, i) => (
                        <div key={i} className={`text-xs px-3 py-2 rounded-lg font-medium ${
                          n.category === 'high_negative'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        }`}>
                          {n.category === 'high_negative' ? '🚩' : '⚠️'} {n.name}
                          {n.distance_str && <span className="ml-1 opacity-60">({n.distance_str})</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Fallback to old arrays if notable not present (backwards compat) */}
                  {!uw.nearby.notable && (uw.nearby.high_negatives?.length > 0 || uw.nearby.mild_negatives?.length > 0) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(uw.nearby.high_negatives || []).map((n, i) => (
                        <div key={`hn-${i}`} className="text-xs px-3 py-2 rounded-lg font-medium bg-red-50 text-red-700 border border-red-200">
                          🚩 {n.name}{n.distance_str ? ` (${n.distance_str})` : ''}
                        </div>
                      ))}
                      {(uw.nearby.mild_negatives || []).map((n, i) => (
                        <div key={`mn-${i}`} className="text-xs px-3 py-2 rounded-lg font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                          ⚠️ {n.name}{n.distance_str ? ` (${n.distance_str})` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-400">
                  {uw.nearby.summary || 'Insufficient nearby data for analysis'}
                </div>
              )}
            </div>
          )}
                  {/* Community services proximity. Shows the five closest plus a count —
              the exposure is how many and how close, not which facility. Keeps the
              block a fixed size: Tampa returns 5 operators, Seattle 15.
              'unavailable' renders nothing; an empty block would imply a clean result. */}
                  {uw.community_services && uw.community_services.status !== 'unavailable' && (
                      <div className="mt-6">
                          <h3 className="font-semibold text-navy text-sm mb-2">Community Services</h3>
                          {uw.community_services.status === 'found' ? (
                              <>
                                  <div className="text-xs text-gray-400 mb-2">
                                      {uw.community_services.meta?.operator_count || 0} facilit
                                      {(uw.community_services.meta?.operator_count || 0) === 1 ? 'y' : 'ies'} identified within {uw.community_services.radius_mi} mi
                                  </div>
                                  {(uw.community_services.operators || []).slice(0, 5).map((o, i) => (
                                      <div key={i} className="flex items-baseline text-sm py-0.5">
                                          <span className="text-gray-500 w-20 shrink-0">
                                              {o.distance_mi.toFixed(2)} mi
                                          </span>
                                          <span className="text-gray-600">
                                              {o.name}
                                              {o.facility_count > 1 && (
                                                  <span className="text-gray-400"> ({o.facility_count} facilities)</span>
                                              )}
                                          </span>
                                      </div>
                                  ))}
                                  {(uw.community_services.operators || []).length > 5 && (
                                      <div className="text-xs text-gray-400 pl-20 py-0.5">
                                          + {uw.community_services.operators.length - 5} more within {uw.community_services.radius_mi} mi
                                      </div>
                                  )}
                              </>
                          ) : (
                              <div className="text-sm text-gray-400">
                                  None identified within {uw.community_services.radius_mi} mi
                              </div>
                          )}
                          <p className="text-xs text-gray-400 italic mt-2">
                              Reflects facilities identified in public business listings within the stated radius. Absence of a result is not evidence that no such facility exists. Domestic violence and other confidential-location facilities are not sought and are suppressed where identified.
                          </p>
                      </div>
                  )}

          {/* Website Scan */}
            {uw.website_scan && !isLro && (
            <div className="mt-6">
              <h3 className="font-semibold text-navy text-sm mb-2">Website Scan</h3>
              {uw.website_scan.scan_successful ? (
                <div className="text-sm text-gray-600">
                  {uw.website_scan.title && <p><strong>Title:</strong> {uw.website_scan.title}</p>}
                  {uw.website_scan.description && <p className="mt-1"><strong>Description:</strong> {uw.website_scan.description}</p>}
                  {uw.website_scan.indicators_found?.length > 0 && (
                    <p className="mt-1 text-red-600">
                      <strong>Flags Found:</strong> {uw.website_scan.indicators_found.join(', ')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-400">
                  {uw.website_scan.is_404 ? (
                    <span className="text-yellow-600">⚠️ {uw.website_scan.uw_note || 'Website not found'}</span>
                  ) : (
                    <span>Website scan unavailable</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION 7: Property Data (add-on) */}
      {/* ============================================ */}
      {prop?.available && prop.parsed && (
        <div className="card mb-6">
          <h2 className="section-title">Property Overview</h2>
          <p className="text-xs text-gray-400 mb-4">Source: HazardHub Property Risk API</p>
                  {report.result?.satellite_image && !isLro && (
                      <div className="mb-4">
                          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Satellite View</div>
                          <img
                              src={report.result.satellite_image}
                              alt="Satellite view of facility"
                              className="w-full rounded-lg border border-gray-200"
                              style={{ maxHeight: '300px', objectFit: 'cover' }}
                          />
                          <p className="text-xs text-gray-400 mt-1">Imagery © Google — approximate facility view</p>
                          {(() => {
                              const t = uw?.storage_vision?.meta?.tile_urls;
                              if (!t) return null;
                              const thumbs = [t.street_view, ...(t.places || [])].filter(Boolean).slice(0, 3);
                              if (thumbs.length === 0) return null;
                              return (
                                  <div className="grid grid-cols-3 gap-2 mt-3">
                                      {thumbs.map((src, i) => (
                                          <img
                                              key={i}
                                              src={src}
                                              alt=""
                                              className="w-full h-32 object-cover rounded border border-gray-200"
                                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                          />
                                      ))}
                                  </div>
                              );
                          })()}
                      </div>
                  )}
          {prop.parsed.property_details && (
            <PropertyTable section={prop.parsed.property_details} />
          )}

                  {prop.parsed?.flood && (() => {
                      const distToCoast = prop.parsed.flood.fields?.distance_to_coast?.value;
                      if (!distToCoast) return null;
                      return (
                          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                              <span className="text-2xl">🌊</span>
                              <div>
                                  <span className="font-semibold text-navy">Distance to Coast: </span>
                                  <span className="text-blue-700 font-bold">{distToCoast}</span>
                              </div>
                          </div>
                      );
                  })()}
                  {(prop.parsed.flood || prop.parsed.fire) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {prop.parsed.flood && <PropertyTable section={prop.parsed.flood} />}
              {prop.parsed.fire && <PropertyTable section={prop.parsed.fire} />}
            </div>
          )}

          {prop.parsed.weather && (
            <div className="mt-6">
              <PropertyTable section={prop.parsed.weather} />
            </div>
          )}

          {prop.parsed.environmental && (
            <div className="mt-6">
              <PropertyTable section={prop.parsed.environmental} />
            </div>
          )}

          {(prop.parsed.services || prop.parsed.crime) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {prop.parsed.services && <PropertyTable section={prop.parsed.services} />}
              {prop.parsed.crime && <PropertyTable section={prop.parsed.crime} />}
            </div>
          )}

          {prop.parsed.liquor_nearby && (
            <div className="mt-6">
              <PropertyTable section={prop.parsed.liquor_nearby} />
            </div>
                  )}
                  {/* Permit history — HazardHub permit SKU, gated with property.
              Fixed-size regardless of permit count: the raw permit list is never
              rendered, only the per-trade rollup plus an unclassified count.
              No total in the header — it would imply arithmetic the table cannot
              close, since most permits on a large property are signage and COs. */}
                  {permits?.coverage === 'covered' && permits.summary && (
                      <div className="mt-6">
                          <div className="bg-navy text-white text-sm font-semibold px-3 py-2 rounded-t-lg">
                              Permit History
                              {permits.summary.earliest_year && permits.summary.latest_year && (
                                  <span className="font-normal text-gray-300 ml-2">
                                      {permits.summary.earliest_year}–{permits.summary.latest_year}
                                  </span>
                              )}
                          </div>
                          <div className="border border-t-0 border-gray-200 rounded-b-lg px-3 py-3">

                              {Object.keys(permits.summary.flags || {}).length > 0 && (
                                  <div className="mb-4">
                                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Notable</div>
                                      {Object.entries(permits.summary.flags).map(([flag, items]) =>
                                          items.map((it, i) => (
                                              <div key={flag + i} className="flex items-start text-sm py-1">
                                                  <span className="text-orange-700 w-32 shrink-0">
                                                      {{ conversion: 'Conversion', fire_damage: 'Fire damage', hazmat: 'Hazardous material', security: 'Security work', unpermitted: 'Unpermitted work' }[flag] || flag}
                                                  </span>
                                                  <span className="text-gray-500 w-28 shrink-0">
                                                      {it.date ? it.date.slice(0, 4) : '—'}
                                                      {it.date_basis === 'applied' && <span className="text-gray-400"> (applied)</span>}
                                                  </span>
                                                  <span className="text-gray-600 w-24 shrink-0 text-right">
                                                      {it.valuation ? '$' + Number(it.valuation).toLocaleString() : ''}
                                                  </span>
                                                  <span className="text-gray-600 flex-1 pl-3">{it.description || ''}</span>
                                              </div>
                                          ))
                                      )}
                                  </div>
                              )}

                              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Tracked Trades</div>
                              {[['roof', 'Roof'], ['hvac', 'HVAC'], ['electrical', 'Electrical'], ['plumbing', 'Plumbing'], ['fire_sprinkler', 'Fire sprinkler'], ['fire_alarm', 'Fire alarm']].map(([key, label]) => {
                                  const t = permits.summary.trades?.[key];
                                  if (!t || !t.found) {
                                      return (
                                          <div key={key} className="flex items-center text-sm py-1">
                                              <span className="text-gray-500 w-32 shrink-0">{label}</span>
                                              {/* "identified", not "on record" — we cannot claim the
                            jurisdiction published nothing, only that we did not
                            confidently map anything to this trade. */}
                                              <span className="text-gray-400">No permit identified</span>
                                          </div>
                                      );
                                  }
                                  return (
                                      <div key={key} className="py-1">
                                          <div className="flex items-center text-sm">
                                              <span className="text-gray-500 w-32 shrink-0">{label}</span>
                                              <span className="text-gray-400 text-xs">{t.count} permit{t.count === 1 ? '' : 's'}</span>
                                          </div>
                                          {t.events.map((e, i) => (
                                              <div key={i} className="flex items-start text-sm py-0.5 pl-32">
                                                  <span className="text-gray-500 w-28 shrink-0">
                                                      {e.date ? e.date.slice(0, 4) : '—'}
                                                      {e.date_basis === 'applied' && <span className="text-gray-400"> (applied)</span>}
                                                  </span>
                                                  <span className="text-gray-600 w-24 shrink-0 text-right">
                                                      {e.valuation ? '$' + Number(e.valuation).toLocaleString() : ''}
                                                  </span>
                                                  <span className="text-gray-500 flex-1 pl-3 text-xs">
                                                      {e.description || ''}
                                                      {e.duplicate_count > 1 && <span className="text-gray-400"> ×{e.duplicate_count}</span>}
                                                  </span>
                                              </div>
                                          ))}
                                          {t.earlier_count > 0 && (
                                              <div className="text-xs text-gray-400 pl-32 py-0.5">+{t.earlier_count} earlier</div>
                                          )}
                                      </div>
                                  );
                              })}
                              {permits.summary.unclassified_count > 0 && (
                                  <div className="flex items-center text-sm py-1">
                                      <span className="text-gray-500 w-32 shrink-0">Unclassified</span>
                                      <span className="text-gray-400 text-xs">{permits.summary.unclassified_count} permit{permits.summary.unclassified_count === 1 ? '' : 's'}</span>
                                  </div>
                              )}

                              {(permits.summary.operators?.length > 0 || permits.summary.operators_undated?.length > 0) && (
                                  <div className="mt-4">
                                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Operator History</div>
                                      {permits.summary.operators?.map((o, i) => (
                                          <div key={i} className="flex items-center text-sm py-0.5">
                                              <span className="text-gray-500 w-32 shrink-0">{o.date.slice(0, 4)}</span>
                                              <span className="text-gray-600">{o.name}</span>
                                          </div>
                                      ))}
                                      {permits.summary.operators_undated?.length > 0 && (
                                          <div className="text-sm text-gray-500 py-0.5">
                                              <span className="text-gray-400">Date not available: </span>
                                              {permits.summary.operators_undated.map(o => o.name).join(' · ')}
                                          </div>
                                      )}
                                  </div>
                              )}

                              <p className="text-xs text-gray-400 italic mt-3">
                                  Permit records reflect what the jurisdiction has published. Absence of a permit is not evidence that work was not performed.
                              </p>
                          </div>
                      </div>
                  )}

                  {permits?.coverage === 'none' && (
                      <div className="mt-6">
                          <div className="bg-navy text-white text-sm font-semibold px-3 py-2 rounded-t-lg">Permit History</div>
                          <div className="border border-t-0 border-gray-200 rounded-b-lg px-3 py-3 text-sm text-gray-400">
                              No permit records available for this jurisdiction.
                          </div>
                      </div>
                  )}
                  
          {prop.address_matched && (
            <div className="mt-4 text-xs text-gray-400 border-t pt-3">
              Matched address: {prop.address_matched}
              {prop.match_score != null && ` (confidence: ${(prop.match_score * 100).toFixed(0)}%)`}
            </div>
                  )}
                  <p className="text-xs text-gray-400 italic mt-3 border-t pt-3">
                      HazardHub property data is provided through Guidewire Software, Inc. for internal use only and is furnished "as is," without warranty of any kind. Guidewire makes no representation as to its accuracy or completeness and is not liable for any loss arising from its use.
                  </p>
        </div>
      )}

      {prop && !prop.available && prop.reason && (
        <div className="card mb-6">
          <h2 className="section-title">Property Overview</h2>
          <div className="text-sm text-gray-400 py-4">⚠️ {prop.reason}</div>
        </div>
      )}

      {/* Charges */}
      {report.charges && report.charges.total > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Report Add-On Charges</span>
            <div className="text-right">
              {report.charges.property_data > 0 && (
                <div className="text-gray-600">Property Data: ${report.charges.property_data.toFixed(2)}</div>
                          )}
                          {report.charges.trip > 0 && (
                              <div className="text-gray-600">TRIP Profile: ${report.charges.trip.toFixed(2)}</div>
                          )}
              <div className="font-bold text-navy mt-1">Total: ${report.charges.total.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function RiskBadge({ score, rag, tierLabel, topRisks = [], favorables = [] }) {
  const ragStyles = {
    green_deep: { bg: 'bg-emerald-100 border-emerald-300', text: 'text-emerald-700' },
    green:      { bg: 'bg-green-50 border-green-200', text: 'text-green-600' },
    yellow:     { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-600' },
    orange:     { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-600' },
    red:        { bg: 'bg-red-50 border-red-200', text: 'text-red-600' },
    gray:       { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-600' }
  };
  const s = ragStyles[rag] || ragStyles.gray;
  
  return (
    <div className={`flex-shrink-0 text-center p-4 rounded-lg border ${s.bg} min-w-[140px]`}>
      <div className={`text-3xl font-bold ${s.text}`}>{score}</div>
      <div className="text-xs font-medium text-gray-500 mt-0.5">Understudy Score</div>
      <div className={`text-xs font-bold mt-1 ${s.text}`}>{tierLabel}</div>
      
      {(topRisks.length > 0 || favorables.length > 0) && (
        <div className="mt-2 text-left text-[10px] leading-tight space-y-0.5 border-t border-gray-200 pt-2">
          {topRisks.slice(0, 3).map((f, i) => (
            <div key={i} className="text-red-600">⚠ {f.label}</div>
          ))}
          {favorables.slice(0, 2).map((f, i) => (
            <div key={i} className="text-green-600">✓ {f.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionIfAnyData({ data, children }) {
  if (!data) return null;
  const hasData = Object.values(data).some(v => v !== null && v !== undefined);
  if (!hasData) return null;
  return <>{children}</>;
}

function BooleanGrid({ title, items }) {
  const filtered = items.filter(i => i.value !== null && i.value !== undefined);
  if (filtered.length === 0) return null;

  return (
    <div className="mb-4">
      {title && <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{title}</div>}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {filtered.map((item, i) => (
          <div key={i} className={`text-center py-2 px-2 rounded-lg text-xs font-medium ${
            item.value === true ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-gray-50 text-gray-400 border border-gray-100'
          }`}>
            <div className="text-[10px] text-gray-400 mb-0.5">{item.label}</div>
            <div>{item.value === true ? '✓ Yes' : '✗ No'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 911 call categories, in underwriting-relevance order rather than the order the
// service happens to emit them. Violence and theft lead because they drive the
// read; medical/fire is usually the largest bucket but is mostly noise for GL.
const CALL_CATEGORIES = [
  { key: 'violence',        label: 'Violence',        valueClass: 'text-red-600' },
  { key: 'theft_property',  label: 'Theft / Property', valueClass: 'text-orange-600' },
  { key: 'disturbance',     label: 'Disturbance',     valueClass: 'text-yellow-600' },
  { key: 'medical_fire',    label: 'Medical / Fire',  valueClass: '' },
  { key: 'police_activity', label: 'Police Activity', valueClass: '' },
  { key: 'other',           label: 'Other',           valueClass: '' },
];

// Format a category count. Sampled figures are extrapolated from a partial read
// and carry a tilde so they are never mistaken for observed counts; exact figures
// are shown plainly because they are facts. A genuine 0 in a covered area is real
// information and IS displayed — only absent data is hidden.
function fmtCalls(n, method) {
  if (n === null || n === undefined) return null;
  const s = typeof n === 'number' ? n.toLocaleString() : String(n);
  return method === 'sampled' ? `~${s}` : s;
}

function DataRow({ label, value, valueClass = '' }) {
  if (value === null || value === undefined) return null;
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium ${valueClass || 'text-navy'}`}>{value}</span>
    </div>
  );
}

function PropertyTable({ section }) {
  if (!section?.fields) return null;
  
  const rows = Object.entries(section.fields)
    .filter(([, f]) => f.value !== null && f.value !== undefined);
  
  if (rows.length === 0) return null;

  return (
    <div>
      <div className="bg-navy text-white text-sm font-semibold px-3 py-2 rounded-t-lg">
        {section.label}
      </div>
      <div className="border border-t-0 border-gray-200 rounded-b-lg divide-y divide-gray-100">
        {rows.map(([key, field]) => (
          <div key={key} className="flex justify-between px-3 py-2 text-sm hover:bg-gray-50">
            <span className="text-gray-500">{field.label}</span>
            <span className="font-medium text-navy text-right max-w-[60%]">
              <PropertyValue value={field.value} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PropertyValue({ value }) {
  if (value === null || value === undefined) return null;
  const str = String(value);

  const gradeMatch = str.match(/^([A-F])\s*[–\-]\s*(.+)$/);
  if (gradeMatch) {
    const grade = gradeMatch[1];
    const text = gradeMatch[2].trim();
    const colors = {
      A: 'bg-green-100 text-green-700 border-green-300',
      B: 'bg-blue-100 text-blue-700 border-blue-300',
      C: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      D: 'bg-orange-100 text-orange-700 border-orange-300',
      E: 'bg-red-100 text-red-700 border-red-300',
      F: 'bg-red-100 text-red-700 border-red-300',
    };
    const c = colors[grade] || 'bg-gray-100 text-gray-700 border-gray-300';
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold border ${c}`}>{grade}</span>
        <span>{text}</span>
      </span>
    );
  }

  if (/^[A-F]$/.test(str.trim())) {
    const grade = str.trim();
    const colors = {
      A: 'bg-green-100 text-green-700 border-green-300',
      B: 'bg-blue-100 text-blue-700 border-blue-300',
      C: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      D: 'bg-orange-100 text-orange-700 border-orange-300',
      F: 'bg-red-100 text-red-700 border-red-300',
    };
    const c = colors[grade] || 'bg-gray-100 text-gray-700 border-gray-300';
    return <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold border ${c}`}>{grade}</span>;
  }

  return <span>{str}</span>;
}
function SecurityRow({ label, detected, risk }) {
    // undefined = field doesn't exist for this LOB → omit the row entirely.
    // null = we looked and could not determine → show it, labeled unknown.
    // Previously null was treated like undefined and the row vanished, which is
    // why the profile emptied out once the scanner stopped asserting false.
    if (detected === undefined) return null;

    let style, icon, text;
    if (detected === null) {
        style = 'text-gray-400';
        icon = '?';
        text = 'Not stated';
    } else if (detected === true && risk) {
        style = 'text-orange-700';
        icon = '⚠';
        text = 'Detected';
    } else if (detected === true) {
        style = 'text-green-700';
        icon = '✓';
        text = 'Detected';
    } else {
        style = 'text-gray-500';
        icon = '—';
        text = 'Not present';
    }

    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">{label}</span>
            <span className={`font-medium ${style}`}>
                {icon} {text}
            </span>
        </div>
    );
}